import { getToken, onMessage, deleteToken, type Messaging } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { firebaseService } from './firebase.service.js';

/**
 * Notification Service
 * Handles Firebase Cloud Messaging (FCM) for push notifications
 */

export interface NotificationToken {
  token: string;
  device_info: {
    browser: string;
    os: string;
    device_type: string;
  };
  created_at: number;
  last_used: number;
}

export interface NotificationSettings {
  enabled: boolean;
  work_orders: boolean;
  preventive_maintenance: boolean;
  safety_reports: boolean;
  system_alerts: boolean;
}

export class NotificationService {
  private messaging: Messaging | null = null;
  private currentToken: string | null = null;
  private userId: string | null = null;

  constructor() {
    this.messaging = firebaseService.getMessaging();
  }

  /**
   * Initialize notification service for a user
   */
  async initialize(userId: string): Promise<void> {
    this.userId = userId;

    if (!this.isSupported()) {
      console.warn('[Notifications] Not supported in this browser');
      return;
    }

    try {
      // Clean up any duplicate tokens before proceeding
      await this.cleanupDuplicateTokens();
      
      // Load existing settings
      const settings = await this.getSettings();
      this.settings = settings;
      
      console.log('[Notifications] Service initialized for user:', userId);
    } catch (error) {
      console.error('[Notifications] Error initializing service:', error);
    }

    // Listen for foreground messages
    this.setupForegroundMessageHandler();
  }

  /**
   * Get the current user ID
   */
  get currentUserId(): string | null {
    return this.userId;
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return this.messaging !== null && 'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error('Notifications not supported in this browser');
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('[Notifications] Permission granted');
        // Get and save the token
        await this.getAndSaveToken();
        return true;
      } else {
        console.log('[Notifications] Permission denied');
        return false;
      }
    } catch (error) {
      console.error('[Notifications] Error requesting permission:', error);
      throw error;
    }
  }

  /**
   * Get FCM token and save to Firestore
   */
  async getAndSaveToken(): Promise<string | null> {
    if (!this.messaging || !this.userId) {
      console.warn('[Notifications] Messaging not initialized or no user ID');
      return null;
    }

    try {
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      
      if (!vapidKey) {
        console.error('[Notifications] VAPID key not found in environment variables');
        return null;
      }

      // Get the service worker registration (our sw.js)
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (!registration) {
        console.error('[Notifications] No service worker registration found');
        return null;
      }

      console.log('[Notifications] Using service worker:', registration.scope);

      // Get FCM token with our service worker registration
      const token = await getToken(this.messaging, { 
        vapidKey,
        serviceWorkerRegistration: registration
      });
      
      if (token) {
        console.log('[Notifications] FCM token obtained');
        this.currentToken = token;
        
        // Save to Firestore
        await this.saveTokenToFirestore(token);
        
        return token;
      } else {
        console.warn('[Notifications] No registration token available');
        return null;
      }
    } catch (error) {
      console.error('[Notifications] Error getting token:', error);
      throw error;
    }
  }

  /**
   * Save FCM token to user document in Firestore
   * Smart token management: prevents duplicates, updates existing tokens
   */
  private async saveTokenToFirestore(token: string): Promise<void> {
    if (!this.userId) {
      throw new Error('No user ID available');
    }

    try {
      const db = firebaseService.getFirestore();
      const userRef = doc(db, 'users', this.userId);

      // Get current user data
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const currentDeviceInfo = {
        browser: this.getBrowserInfo(),
        os: this.getOSInfo(),
        device_type: this.getDeviceType()
      };

      const newTokenData: NotificationToken = {
        token,
        device_info: currentDeviceInfo,
        created_at: Date.now(),
        last_used: Date.now()
      };

      let updatedTokens: NotificationToken[] = [];

      if (userData?.fcm_tokens && Array.isArray(userData.fcm_tokens)) {
        // Check if token for this device already exists
        const existingTokenIndex = userData.fcm_tokens.findIndex((existingToken: NotificationToken) => {
          return existingToken.token === token || 
                 (existingToken.device_info.browser === currentDeviceInfo.browser &&
                  existingToken.device_info.os === currentDeviceInfo.os &&
                  existingToken.device_info.device_type === currentDeviceInfo.device_type);
        });

        if (existingTokenIndex >= 0) {
          // Update existing token
          updatedTokens = [...userData.fcm_tokens];
          updatedTokens[existingTokenIndex] = newTokenData;
          console.log('[Notifications] Updated existing token for device');
        } else {
          // Add new token for new device
          updatedTokens = [...userData.fcm_tokens, newTokenData];
          console.log('[Notifications] Added new token for new device');
        }
      } else {
        // First token for this user
        updatedTokens = [newTokenData];
        console.log('[Notifications] Added first token for user');
      }

      // Update Firestore with cleaned tokens and enable notifications
      await updateDoc(userRef, {
        fcm_tokens: updatedTokens,
        'notification_settings.enabled': true
      });

      console.log('[Notifications] Token saved to Firestore successfully');
    } catch (error) {
      console.error('[Notifications] Error saving token to Firestore:', error);
      throw error;
    }
  }

  /**
   * Delete FCM token (when user disables notifications)
   * Actually, we don't delete tokens - we just disable notifications
   * This keeps tokens for when user re-enables notifications
   */
  async deleteCurrentToken(): Promise<void> {
    if (!this.messaging || !this.currentToken) {
      return;
    }

    try {
      // Don't actually delete the token from device
      // Just disable notifications in settings
      if (this.userId) {
        await this.updateSettings({ enabled: false });
        console.log('[Notifications] Notifications disabled (tokens kept for re-enabling)');
      }
      
      this.currentToken = null;
    } catch (error) {
      console.error('[Notifications] Error disabling notifications:', error);
      throw error;
    }
  }

  /**
   * Remove token from Firestore
   */
  private async removeTokenFromFirestore(token: string): Promise<void> {
    if (!this.userId) return;

    try {
      const db = firebaseService.getFirestore();
      const userRef = doc(db, 'users', this.userId);
      
      // Get current tokens
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      if (userData?.fcm_tokens) {
        // Find and remove the matching token
        const updatedTokens = userData.fcm_tokens.filter(
          (t: NotificationToken) => t.token !== token
        );
        
        await updateDoc(userRef, {
          fcm_tokens: updatedTokens
        });
      }
      
      console.log('[Notifications] Token removed from Firestore');
    } catch (error) {
      console.error('[Notifications] Error removing token from Firestore:', error);
    }
  }

  /**
   * Update notification settings in Firestore
   */
  async updateSettings(settings: Partial<NotificationSettings>): Promise<void> {
    if (!this.userId) {
      throw new Error('No user ID available');
    }

    try {
      const db = firebaseService.getFirestore();
      const userRef = doc(db, 'users', this.userId);

      // Build update object with dot notation
      const updates: any = {};
      Object.keys(settings).forEach(key => {
        updates[`notification_settings.${key}`] = settings[key as keyof NotificationSettings];
      });

      await updateDoc(userRef, updates);
      
      // Update local settings cache
      if (this.settings) {
        this.settings = { ...this.settings, ...settings };
      }
      
      console.log('[Notifications] Settings updated successfully:', settings);
    } catch (error) {
      console.error('[Notifications] Error updating settings:', error);
      throw error;
    }
  }

  /**
   * Get notification settings from Firestore
   */
  async getSettings(): Promise<NotificationSettings | null> {
    if (!this.userId) {
      return null;
    }

    try {
      const db = firebaseService.getFirestore();
      const userRef = doc(db, 'users', this.userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const settings = data?.notification_settings;
        
        if (settings) {
          console.log('[Notifications] Loaded settings from Firestore:', settings);
          return settings;
        }
      }
      
      console.log('[Notifications] No settings found, using defaults');
      return this.getDefaultSettings();
    } catch (error) {
      console.error('[Notifications] Error getting settings:', error);
      return this.getDefaultSettings();
    }
  }

  /**
   * Clean up duplicate tokens in Firestore
   */
  private async cleanupDuplicateTokens(): Promise<void> {
    if (!this.userId) {
      return;
    }

    try {
      const db = firebaseService.getFirestore();
      const userRef = doc(db, 'users', this.userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const tokens: NotificationToken[] = userData?.fcm_tokens || [];
        
        if (tokens.length > 1) {
          // Remove duplicates based on token string
          const uniqueTokens = tokens.reduce((acc: NotificationToken[], current: NotificationToken) => {
            const isDuplicate = acc.some(token => token.token === current.token);
            if (!isDuplicate) {
              acc.push(current);
            }
            return acc;
          }, []);
          
          // Only update if we found duplicates
          if (uniqueTokens.length !== tokens.length) {
            await updateDoc(userRef, {
              fcm_tokens: uniqueTokens
            });
            console.log(`[Notifications] Cleaned up ${tokens.length - uniqueTokens.length} duplicate tokens`);
          }
        }
      }
    } catch (error) {
      console.error('[Notifications] Error cleaning up duplicate tokens:', error);
    }
  }

  /**
   * Get default notification settings
   */
  private getDefaultSettings(): NotificationSettings {
    return {
      enabled: false,
      work_orders: true,
      preventive_maintenance: true,
      safety_reports: true,
      system_alerts: false
    };
  }

  /**
   * Setup handler for foreground messages (when app is open)
   */
  private setupForegroundMessageHandler(): void {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {
      console.log('[Notifications] Foreground message received:', payload);
      
      // Show notification using Notification API
      if (payload.notification) {
        this.showNotification(
          payload.notification.title || 'New Notification',
          payload.notification.body || '',
          payload.data
        );
      }
    });
  }

  /**
   * Show browser notification
   */
  private showNotification(title: string, body: string, data?: any): void {
    if (Notification.permission !== 'granted') {
      return;
    }

    const options: NotificationOptions = {
      body,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      data,
      tag: data?.type || 'general',
      requireInteraction: false
    };

    const notification = new Notification(title, options);

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      
      // Navigate to relevant page based on notification data
      if (data?.url) {
        window.location.href = data.url;
      }
      
      notification.close();
    };
  }

  /**
   * Send a test notification (for testing purposes)
   * This creates a proper system notification that appears in macOS notification center
   */
  async sendTestNotification(): Promise<void> {
    console.log('[Notifications] === DIAGNOSTIC START ===');
    console.log('[Notifications] Permission status:', Notification.permission);
    console.log('[Notifications] Notification constructor available:', typeof Notification);
    console.log('[Notifications] Browser:', navigator.userAgent);
    
    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    try {
      // Create a proper system notification
      console.log('[Notifications] Creating notification object...');
      const notification = new Notification('🔔 PristineMaint Test', {
        body: 'This is a test notification from PristineMaint CMMS. If you can see this, system notifications are working!',
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: 'pristinemaint-test',
        requireInteraction: false,
        silent: false, // Allow sound
        data: { 
          type: 'test',
          url: window.location.href
        }
      });

      console.log('[Notifications] Notification object created:', notification);
      console.log('[Notifications] Notification properties:');
      console.log('  - title:', notification.title);
      console.log('  - body:', notification.body);
      console.log('  - tag:', notification.tag);
      console.log('  - icon:', notification.icon);

      // Handle notification click
      notification.onclick = (event) => {
        console.log('[Notifications] Notification clicked!');
        event.preventDefault();
        window.focus();
        notification.close();
      };

      // Auto-close after 8 seconds (longer for testing)
      setTimeout(() => {
        console.log('[Notifications] Auto-closing notification...');
        notification.close();
      }, 8000);

      console.log('[Notifications] Test system notification sent successfully');
      console.log('[Notifications] === DIAGNOSTIC END ===');
    } catch (error) {
      console.error('[Notifications] Error sending test notification:', error);
      console.log('[Notifications] === DIAGNOSTIC END (ERROR) ===');
      throw error;
    }
  }

  /**
   * Refresh token (update last_used timestamp)
   */
  async refreshToken(): Promise<void> {
    if (!this.currentToken || !this.userId) {
      return;
    }

    try {
      const db = firebaseService.getFirestore();
      const userRef = doc(db, 'users', this.userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        if (userData?.fcm_tokens) {
          const updatedTokens = userData.fcm_tokens.map((t: NotificationToken) => {
            if (t.token === this.currentToken) {
              return { ...t, last_used: Date.now() };
            }
            return t;
          });
          
          await updateDoc(userRef, {
            fcm_tokens: updatedTokens
          });
        }
      }
    } catch (error) {
      console.error('[Notifications] Error refreshing token:', error);
    }
  }

  // Helper methods for device info

  private getBrowserInfo(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    
    return 'Unknown';
  }

  private getOSInfo(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Win')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
    
    return 'Unknown';
  }

  private getDeviceType(): string {
    const userAgent = navigator.userAgent;
    
    if (/mobile/i.test(userAgent)) return 'Mobile';
    if (/tablet|ipad/i.test(userAgent)) return 'Tablet';
    
    return 'Desktop';
  }
}

// Create and export singleton instance
export const notificationService = new NotificationService();



